import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IClub } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import TextArea from "src/components/TextArea/TextArea";
import { notifyPostCreated, showPostError } from "src/routes/Discover/postFeedback";

// Same layout and styling as the owner photo composer; only the rules differ
// (club author, photo optional, caption-or-photo required).
import styles from "src/routes/Discover/MomentComposer/MomentComposer.module.scss";

const MAX_CAPTION = 2000;

interface IProps {
    club: IClub;
    onClose: () => void;
    onSuccess: () => void;
}

/**
 * Club update composer for managers, opened from the club page's Activity
 * tab. Posts as the club (name + logo) through POST clubs/:clubId/posts,
 * which checks "manage" permission and screens the content.
 */
const ClubPostComposer: React.FC<IProps> = ({ club, onClose, onSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>("");
    const [caption, setCaption] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] || null);
    };

    const canPost = !!caption.trim() || !!file;

    const handleSubmit = async () => {
        if (!canPost) {
            toast.show({
                title: "Post an update",
                content: "Add a caption or a photo.",
                duration: 6000,
                type: "fail",
            });
            return;
        }

        setSubmitting(true);
        try {
            let media: { type: "image"; url: string }[] | undefined;
            if (file) {
                const uploaded: any = await apiService.uploadFile(file);
                const url = typeof uploaded === "string" ? uploaded : uploaded?.value;
                if (typeof url !== "string" || !url) {
                    throw new Error("Upload did not return a file URL");
                }
                media = [{ type: Constants.post_media_type.image, url }];
            }
            const created = await apiService.createClubPost(club._id, {
                post_type: Constants.post_type.club_update,
                body: caption.trim() || undefined,
                media,
            });
            notifyPostCreated(created);
            onSuccess();
            onClose();
        } catch (err) {
            showPostError(err, {
                title: "Post failed",
                content: "Unable to post right now. Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.MomentComposer}>
            <div className={styles.title}>Post an update</div>

            <div className={styles.posterRow}>
                <div className={styles.avatar}>
                    {club.logo_url
                        ? <img src={club.logo_url} alt={club.club_name} />
                        : <span>{club.club_name.charAt(0).toUpperCase()}</span>}
                </div>
                <div className={styles.posterText}>
                    <div className={styles.posterName}>{club.club_name}</div>
                    <div className={styles.posterSub}>{`Posting to ${club.club_name}’s followers`}</div>
                </div>
            </div>

            <TextArea
                label="Update"
                placeholder={`What's new at ${club.club_name}?`}
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
                className={styles.captionField}
            />

            <label className={styles.uploadArea}>
                <input type="file" accept="image/*" onChange={onSelectFile} />
                {previewUrl ? (
                    <img src={previewUrl} alt="" className={styles.preview} />
                ) : (
                    <>
                        <div className={styles.uploadIcon}>↑</div>
                        <div className={styles.uploadTitle}>Add a photo (optional)</div>
                        <div className={styles.uploadHint}>JPG or PNG up to 20 MB</div>
                        <span className={styles.chooseFileBtn}>Choose file</span>
                    </>
                )}
            </label>

            <div className={styles.footer}>
                <div className={styles.footerActions}>
                    <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={!canPost} isLoading={submitting}>Post</Button>
                </div>
            </div>
        </div>
    );
};

export default ClubPostComposer;
